import React, {useEffect, useState} from "react";
import {GAME_SHOT_CHARTS} from "../../../../widgets/game-shot-chart-widget/game-shot-charts.widget.consts";

export const ShotMapShotAnimationComposer = (Widget) => ({
                                                           controller,
                                                           width,
                                                           ratio,
                                                           selectedShot,
                                                           shouldRenderAnimation,
                                                           shotSideOffset,
                                                           shotLineOffset
                                                         }) => {

  if (!shouldRenderAnimation) {
    return <Widget controller={controller} width={width}/>
  }


  const {device: {isRtl}} = controller;
  const [circle, setCircle] = useState(null)
  const [endPoint, setEndPoint] = useState(null)
  const [startPoint, setStartPoint] = useState(null)
  const [blockVector, setBlockVector] = useState(null)
  const [isAnimationActive, setIsAnimationActive] = useState(false)
  const [shouldRenderLine, setShouldRenderLine] = useState(false);

  const height: Number = parseInt(width / ratio);
  const sideOffset: Number = (width * shotSideOffset) / 100;
  const lineOffset: Number = height * shotLineOffset / 100;
  const shotCircleRadius: Number = GAME_SHOT_CHARTS.RADIUS.shot;
  const shotAnimationCircleRadius: Number = GAME_SHOT_CHARTS.RADIUS.animation;

  useEffect(() => {
    if (selectedShot) {
      setIsAnimationActive(false);

      const circle = drawCircle();
      if (selectedShot.outcome.hasShotCoordination) {
        const line = drawOutcome();
        const blockLine = drawBlockLine(line.x, line.y, circle.x, circle.y);
        const isBlockLineIntersect = circleLineIntersection([blockLine.x1, blockLine.y1], [blockLine.x2, blockLine.y2], circle.x, circle.y);
        setShouldRenderLine(!isBlockLineIntersect);
      } else {
        setShouldRenderLine(false);
      }


      setTimeout(() => setIsAnimationActive(true), 100);
    }
  }, [selectedShot?.key])

  const drawCircle = () => {
    const cpSide = parseInt(selectedShot.side * sideOffset)
    const cpLine = parseInt(selectedShot.line * lineOffset)

    const circleX1 = width - cpSide - shotCircleRadius;
    const circleX2 = cpSide + shotCircleRadius;
    const circleY1 = cpLine + shotCircleRadius;
    const circleY2 = height - cpLine - shotCircleRadius;
    const cx: Number = selectedShot.isHomeCompetitor ? (isRtl ? circleX2 : circleX1) : (isRtl ? circleX1 : circleX2);
    const cy: Number = selectedShot.isHomeCompetitor ? (isRtl ? circleY2 : circleY1) : (isRtl ? circleY1 : circleY2);
    setCircle({x: cx, y: cy})

    return {x: cx, y: cy}
  }

  const drawOutcome = () => {
    const offset: Number = parseInt((width * (1 - shotSideOffset)) / 2) + 2
    const outcomeX: Number = parseInt(selectedShot.outcome.x * sideOffset) + shotCircleRadius;
    const outcomeY: Number = parseInt(selectedShot.outcome.y * lineOffset) + shotCircleRadius;

    const leftSideX: Number = selectedShot.isBlockedShot || selectedShot.isOwnGoalShot ? width - (outcomeX || offset) : outcomeX || offset;
    const leftSideY: Number = outcomeY;

    const rightSideX: Number = selectedShot.isOwnGoalShot ? offset : outcomeX || width - offset;
    const rightSideY: Number = height - outcomeY;

    const endPointX: Number = selectedShot.isReverseSide ? rightSideX : leftSideX;
    const endPointY: Number = selectedShot.isReverseSide ? (selectedShot.isOwnGoalShot ? leftSideY : rightSideY) : (selectedShot.isOwnGoalShot ? rightSideY : leftSideY);

    setEndPoint({x: endPointX, y: endPointY})

    return {x: endPointX, y: endPointY}
  }

  const drawBlockLine = (lx, ly, cx, cy) => {
    const directionVector = [lx - cx, ly - cy];
    const length = Math.sqrt(directionVector[0] ** 2 + directionVector[1] ** 2);
    const normalizedVector = [directionVector[0] / length, directionVector[1] / length];

    const startPointX = parseInt(cx + normalizedVector[0] * shotAnimationCircleRadius);
    const startPointY = parseInt(cy + normalizedVector[1] * shotAnimationCircleRadius);
    setStartPoint({x: startPointX, y: startPointY});

    const blockLineLength = 8;
    const midX = lx, midY = ly;
    const dx = lx - startPointX, dy = ly - startPointY;
    const len = Math.hypot(dx, dy);
    const nx = dx / len, ny = dy / len;
    const px = -ny, py = nx;

    const ax = midX - px * (blockLineLength / 2);
    const ay = midY - py * (blockLineLength / 2);
    const bx = midX + px * (blockLineLength / 2);
    const by = midY + py * (blockLineLength / 2);

    setBlockVector({x1: ax, y1: ay, x2: bx, y2: by})

    return {x1: ax, y1: ay, x2: bx, y2: by}
  }

  const circleLineIntersection = (point1, point2, circleX, circleY) => {
    const [x1, y1] = point1;
    const [x2, y2] = point2;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const A = (dx * dx) + (dy * dy);
    const B = 2 * (dx * (x1 - circleX) + dy * (y1 - circleY));
    const C = (x1 - circleX) * (x1 - circleX) + (y1 - circleY) * (y1 - circleY) - shotAnimationCircleRadius * shotAnimationCircleRadius;
    const discriminant = B * B - 4 * A * C;

    return discriminant >= 0;
  }


  const {state: {game}} = controller;
  const {homeCompetitor, awayCompetitor} = game;
  const color = selectedShot?.isHomeCompetitor ? homeCompetitor.color : awayCompetitor.color

  const newProps = {
    width,
    ratio,
    circle,
    isAnimationActive,
    shouldRenderAnimation,
    controller,
    endPoint: shouldRenderLine ? endPoint : null,
    startPoint: shouldRenderLine ? startPoint : null,
    blockVector: selectedShot?.isBlockedShot && shouldRenderLine ? blockVector : null,
    color
  }


  if (selectedShot === null) {
    return <Widget controller={controller} width={width}/>
  } else {
    return <Widget {...newProps}/>
  }
};
